import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, finalize, takeUntil } from 'rxjs';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { AppService } from '@services/app.service';
import { ExpenseDetails } from '@/models/expense-details/expense-details';
import { DailyExpense } from '@/models/expense-details/daily-expense';
@Component({
  selector: 'app-daily-expense-view',
  templateUrl: './daily-expense-view.component.html',
  styleUrl: './daily-expense-view.component.scss'
})
export class DailyExpenseViewComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  expenses: DailyExpense[] = [];
  expenseMasters: ExpenseDetails[] = [];

  startDate = '';
  endDate = '';
  selectedExpenseSeq: number | '' = '';
  searchText = '';

  loading = false;
  validationMessage = '';

  readonly today = this.formatDateInput(new Date());

  constructor(
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const today = new Date();

    // Default: first day of the current month through today.
    const firstDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );

    this.startDate = this.formatDateInput(firstDay);
    this.endDate = this.today;

    this.loadExpenseMasters();
    this.searchByDate();
  }

  // -----------------------------------------------
  // Load active expense master records
  // -----------------------------------------------

  private loadExpenseMasters(): void {
    this.appService.getAllExpenseDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const items = Array.isArray(response)
            ? response
            : response?.data ?? [];

          this.expenseMasters = items
            .filter(
              (item: ExpenseDetails) =>
                (item.status || '').toUpperCase() === 'ACTIVE'
            )
            .sort(
              (a: ExpenseDetails, b: ExpenseDetails) =>
                a.name.localeCompare(b.name)
            );
        },
        error: (error: any) => {
          console.error('Expense master error:', error);
          this.toastr.error('Unable to load expense names.');
        }
      });
  }

  // -----------------------------------------------
  // Date changes: automatically load API results
  // -----------------------------------------------

  onDateChange(): void {
    this.validationMessage = '';

    // Wait until both dates have been selected.
    if (!this.startDate || !this.endDate) {
      return;
    }

    if (!this.validateDateRange()) {
      return;
    }

    this.searchByDate();
  }

  private validateDateRange(): boolean {
    if (!this.startDate || !this.endDate) {
      this.validationMessage = 'Please select both dates.';
      return false;
    }

    const start = this.parseDate(this.startDate);
    const end = this.parseDate(this.endDate);
    const today = this.parseDate(this.today);

    if (!start || !end || !today) {
      this.validationMessage = 'Please select valid dates.';
      return false;
    }

    if (start.getTime() > today.getTime()) {
      this.validationMessage = 'From Date cannot be in the future.';
      return false;
    }

    if (end.getTime() > today.getTime()) {
      this.validationMessage = 'To Date cannot be in the future.';
      return false;
    }

    if (start.getTime() > end.getTime()) {
      this.validationMessage =
        'From Date cannot be later than To Date.';
      return false;
    }

    this.validationMessage = '';
    return true;
  }

  // -----------------------------------------------
  // Search API using epoch milliseconds
  // -----------------------------------------------

  searchByDate(): void {
    if (this.loading) {
      return;
    }

    if (!this.validateDateRange()) {
      this.toastr.warning(this.validationMessage);
      return;
    }

    const start = this.parseDate(this.startDate);
    const end = this.parseDate(this.endDate);

    if (!start || !end) {
      return;
    }

    const startTimestamp = start.getTime();

    // Include the entire To Date.
    const endTimestamp = end.getTime() + 24 * 60 * 60 * 1000 - 1;

    this.loading = true;
    this.spinner.show();

    this.appService
      .getDailyExpensesByDateRange(startTimestamp, endTimestamp)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (response: any) => {
          const items: DailyExpense[] = Array.isArray(response)
            ? response
            : response?.data ?? [];

          this.expenses = items;

          // hasBill may be absent from the date-range API.
          // Use available bill information when present.
          this.expenses = this.expenses.map(item => ({
            ...item,
            hasBill: Boolean(
              item.hasBill || item.billPath
            )
          }));
        },
        error: (error: any) => {
          console.error('Daily expense search error:', error);
          this.expenses = [];

          this.toastr.error(
            error?.error?.message ||
            'Unable to load daily expenses.'
          );
        }
      });
  }

  // -----------------------------------------------
  // Apply expense name and text search filters
  // -----------------------------------------------

  get filteredExpenses(): DailyExpense[] {
    const search = this.searchText.trim().toLowerCase();

    return this.expenses.filter(item => {
      const expenseMatches =
        this.selectedExpenseSeq === '' ||
        Number(item.expenseSeq) === Number(this.selectedExpenseSeq);

      const textMatches =
        !search ||
        (item.expenseName || '').toLowerCase().includes(search) ||
        (item.description || '').toLowerCase().includes(search);

      return expenseMatches && textMatches;
    });
  }

  get totalAmount(): number {
    return this.filteredExpenses.reduce(
      (total, item) => total + Number(item.amount || 0),
      0
    );
  }

  // -----------------------------------------------
  // Reset filters
  // -----------------------------------------------

  resetFilters(): void {
    const today = new Date();

    this.startDate = this.formatDateInput(
      new Date(today.getFullYear(), today.getMonth(), 1)
    );

    this.endDate = this.today;
    this.selectedExpenseSeq = '';
    this.searchText = '';
    this.validationMessage = '';

    this.searchByDate();
  }

  // -----------------------------------------------
  // Edit expense
  // -----------------------------------------------

  editExpense(item: DailyExpense): void {
    if (!item.dailyExpenseId) {
      this.toastr.error('Daily expense ID is missing.');
      return;
    }

    localStorage.setItem(
      'dailyExpenseId',
      String(item.dailyExpenseId)
    );

    this.router.navigate(['/main/daily-expense-entry']);
  }

  // -----------------------------------------------
  // Delete entire expense
  // -----------------------------------------------

  deleteExpense(item: DailyExpense): void {
    if (!item.dailyExpenseId) {
      this.toastr.error('Daily expense ID is missing.');
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: 'This daily expense will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }

      this.loading = true;
      this.spinner.show();

      this.appService
        .deleteDailyExpense(item.dailyExpenseId)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.loading = false;
            this.spinner.hide();
          })
        )
        .subscribe({
          next: () => {
            this.expenses = this.expenses.filter(
              expense =>
                expense.dailyExpenseId !== item.dailyExpenseId
            );

            Swal.fire({
              title: 'Deleted!',
              text: 'Daily expense deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#198754'
            });
          },
          error: (error: any) => {
            console.error('Delete expense error:', error);

            Swal.fire({
              title: 'Error!',
              text:
                error?.error?.message ||
                'Unable to delete daily expense.',
              icon: 'error'
            });
          }
        });
    });
  }

  // -----------------------------------------------
  // Excel export - exports currently filtered results
  // -----------------------------------------------

  exportExcel(): void {
    const data = this.filteredExpenses;

    if (!data.length) {
      this.toastr.warning('No expense records available to export.');
      return;
    }

    const exportData = data.map((item, index) => ({
      'S.No': index + 1,
      'Expense Date': this.formatDisplayDate(item.expenseDate),
      'Expense Name': item.expenseName,
      Description: item.description,
      Amount: Number(item.amount || 0),
      Bill: item.hasBill ? 'Attached' : 'No bill'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Daily Expenses'
    );

    XLSX.writeFile(
      workbook,
      `Daily_Expense_Report_${this.startDate}_to_${this.endDate}.xlsx`
    );
  }

  // -----------------------------------------------
  // Date helpers
  // -----------------------------------------------

  private parseDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const parts = value.split('-').map(Number);

    if (parts.length !== 3) {
      return null;
    }

    const date = new Date(
      parts[0],
      parts[1] - 1,
      parts[2]
    );

    if (
      date.getFullYear() !== parts[0] ||
      date.getMonth() !== parts[1] - 1 ||
      date.getDate() !== parts[2]
    ) {
      return null;
    }

    return date;
  }

  private formatDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  formatDisplayDate(timestamp: number | string): string {
    let value = Number(timestamp);

    // Support epoch seconds as well as epoch milliseconds.
    if (value > 0 && value < 100000000000) {
      value *= 1000;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}